/**
 * Phase 0 HUD: FPS, frame time, audio meters, beat diagnostics, current
 * preset, internal resolution, present-mode and auto-beat-test status.
 *
 * The beat block was rebuilt to actually be useful at the HUD's 100 ms
 * refresh: a one-frame `*` at 60 Hz is invisible. Instead we latch the
 * indicator for ~160 ms after every tick, show the rolling BPM, mark the
 * source ('real' detector vs synthetic injector), and draw a small gauge of
 * the current flux against the adaptive threshold so the user can see why
 * a beat did or didn't fire.
 *
 * Updates at most ~10 Hz so it doesn't dominate the profile.
 */

const HUD_REFRESH_MS = 100;
const BEAT_LATCH_MS = 160;

export type HudBeatInput = {
  tick: boolean;
  /** 'real' = detector, 'synth' = injector, 'none' = no tick this frame. */
  source: 'real' | 'synth' | 'none';
  confidence: number;
  flux: number;
  threshold: number;
  bpm: number | null;
  msSinceLastBeat: number | null;
};

export type HudAutoInput = {
  enabled: boolean;
  currentChildId: string | null;
  msUntilNextTarget: number | null;
  waitingForBeat: boolean;
  forceImminent: boolean;
};

export type HudInput = {
  fps: number;
  frameMs: number;
  preset: string;
  internalScale: number;
  internalSize: [number, number];
  rms: number;
  bass: number;
  mid: number;
  treble: number;
  beat: HudBeatInput;
  audioSource: string;
  presentMode: string;
  autoBeatMs: number | null;
  /** Auto-cycler status. `enabled: false` when the user picked a preset directly. */
  auto: HudAutoInput;
  /** Crossfade progress in [0,1], or null when no transition is in flight. */
  transitionT?: number | null;
  /** Warp-field state when one is active. */
  warpField?: {
    generator: 'curl' | 'spiral' | 'swirl' | 'julia';
    t: number;
    inTransition: boolean;
    msSinceLastAdoption: number | null;
  } | null;
};

export class Hud {
  private el: HTMLElement;
  private lastUpdate = 0;
  private visible = true;
  private lastBeatAtMs = -Infinity;
  private lastBeatSource: 'real' | 'synth' = 'real';

  constructor() {
    const el = document.getElementById('hud');
    if (!el) throw new Error('#hud missing from DOM');
    this.el = el;
    this.show();
  }

  toggle(): void {
    this.visible = !this.visible;
    if (this.visible) this.show();
    else this.hide();
  }

  show(): void {
    this.el.classList.remove('hidden');
    this.visible = true;
  }

  hide(): void {
    this.el.classList.add('hidden');
    this.visible = false;
  }

  update(now: number, info: HudInput): void {
    // Latch beat state on every frame, even when the HUD itself isn't ready
    // to redraw. Otherwise a tick that lands between two HUD redraws would be
    // lost from the indicator entirely.
    if (info.beat.tick && info.beat.source !== 'none') {
      this.lastBeatAtMs = now;
      this.lastBeatSource = info.beat.source;
    }

    if (!this.visible) return;
    if (now - this.lastUpdate < HUD_REFRESH_MS) return;
    this.lastUpdate = now;

    const fps = info.fps.toFixed(1).padStart(5);
    const ms = info.frameMs.toFixed(2).padStart(5);
    const [iw, ih] = info.internalSize;
    const trans =
      info.transitionT === null || info.transitionT === undefined
        ? ''
        : `\nxfade: ${(info.transitionT * 100).toFixed(0)}%`;
    let field = '';
    if (info.warpField) {
      const wf = info.warpField;
      const since =
        wf.msSinceLastAdoption === null
          ? '—'
          : `${(wf.msSinceLastAdoption / 1000).toFixed(1)}s`;
      const fieldT = wf.t.toFixed(2);
      const phase = wf.inTransition ? `adopting (t=${fieldT})` : 'stable';
      field = `\nfield: ${wf.generator} · ${phase} · last ${since}`;
    }

    const presetLine = renderPresetLine(info);

    this.el.textContent =
      `${fps} fps  ${ms} ms\n` +
      `${iw}x${ih} (scale ${info.internalScale.toFixed(2)})\n` +
      `${presetLine}${trans}${field}\n` +
      `audio:  ${info.audioSource}\n` +
      `RMS  ${bar(info.rms)}\n` +
      `bass ${bar(info.bass)}\n` +
      `mid  ${bar(info.mid)}\n` +
      `treb ${bar(info.treble)}\n` +
      this.renderBeatBlock(now, info);
  }

  private renderBeatBlock(now: number, info: HudInput): string {
    const sinceLatchMs = now - this.lastBeatAtMs;
    const lit = sinceLatchMs < BEAT_LATCH_MS;
    // Big sticky pulse so the user can actually see beats. Use a full block
    // so it's obvious even at small font sizes.
    const indicator = lit ? '\u25CF' : '\u00B7';
    const sourceLabel = lit ? `(${this.lastBeatSource})` : '       ';

    // Flux vs threshold gauge: 0..1 maps the threshold to a fixed mid-line at
    // 50% so any bar past the midpoint corresponds to flux > threshold (a
    // potential beat, modulo refractory). This makes the threshold visually
    // obvious without needing two separate gauges.
    const fluxRel = relativeToThreshold(info.beat.flux, info.beat.threshold);
    const gauge = thresholdGauge(fluxRel);

    const bpmStr =
      info.beat.bpm === null ? '   --' : `${info.beat.bpm.toFixed(0).padStart(5)}`;
    const sinceStr =
      info.beat.msSinceLastBeat === null
        ? '   -- ms'
        : `${Math.round(info.beat.msSinceLastBeat).toString().padStart(5)} ms`;

    const auto = info.autoBeatMs === null ? 'off' : `${info.autoBeatMs}ms`;
    const conf = info.beat.confidence.toFixed(2);
    const flux = info.beat.flux.toFixed(3);
    const thr = info.beat.threshold.toFixed(3);

    return (
      `beat ${indicator} ${sourceLabel}  bpm ${bpmStr}  Δ ${sinceStr}\n` +
      `flux ${gauge} ${flux}/${thr} (conf ${conf})\n` +
      `auto-beat: ${auto}\n` +
      `present: ${info.presentMode}`
    );
  }
}

/**
 * Render the "preset:" line. In auto mode this becomes
 *   `preset: auto -> curl_flow  next ~9.2s`
 *   `preset: auto -> spiral_orbit  beat-locked (force ~1.1s)`
 * so the user can see (a) that auto is on, (b) which child is currently
 * being rendered, and (c) when the next switch is expected.
 */
function renderPresetLine(info: HudInput): string {
  if (!info.auto.enabled) {
    return `preset: ${info.preset}`;
  }
  const child = info.auto.currentChildId ?? info.preset;
  const ms = info.auto.msUntilNextTarget;
  let suffix: string;
  if (info.auto.waitingForBeat) {
    suffix = info.auto.forceImminent ? 'beat-locked (force soon)' : 'beat-locked';
  } else if (ms === null) {
    suffix = '';
  } else if (ms <= 0) {
    suffix = 'switching…';
  } else {
    const s = (ms / 1000).toFixed(1);
    suffix = info.auto.forceImminent ? `force ~${s}s` : `next ~${s}s`;
  }
  return `preset: auto → ${child}${suffix ? '  ' + suffix : ''}`;
}

function bar(v: number): string {
  const clamped = Math.min(1, Math.max(0, v));
  const filled = Math.round(clamped * 20);
  return '[' + '#'.repeat(filled) + '-'.repeat(20 - filled) + ']';
}

/**
 * Map (flux, threshold) -> 0..1 where 0.5 means flux == threshold.
 * Below threshold compresses the lower half; above threshold expands the
 * upper half so it's visually obvious when we're firing.
 */
function relativeToThreshold(flux: number, threshold: number): number {
  if (threshold <= 0) return flux > 0 ? 1 : 0;
  if (flux <= 0) return 0;
  if (flux <= threshold) {
    return 0.5 * (flux / threshold);
  }
  // flux > threshold: stretch the (1..) range over the upper half.
  const overshoot = (flux - threshold) / threshold; // 0 == threshold, 1 == 2x
  return 0.5 + 0.5 * Math.min(1, overshoot);
}

/**
 * 20-cell gauge with a mid-line marker at the threshold (cell 10).
 * Cells filled with `#` to the right of the marker indicate above-threshold.
 */
function thresholdGauge(rel: number): string {
  const cells = 20;
  const filled = Math.max(0, Math.min(cells, Math.round(rel * cells)));
  let out = '';
  for (let i = 0; i < cells; i++) {
    if (i === cells / 2) {
      out += '|';
    } else if (i < filled) {
      out += '#';
    } else {
      out += '-';
    }
  }
  return '[' + out + ']';
}
